import { useCallback, useState } from "react";
import Text from "@ui-kit/typography/Text/Text.web";
import Box from "@ui-kit/layout/Box/Box.web";
import { CardBrandLogoProps } from "@shared/modules/payments/common/CardBrandLogo/CardBrandLogo.props";
import CardBrandLogo from "@shared/modules/payments/common/CardBrandLogo/CardBrandLogo.web";
import { CardNumberChangeEvent } from "@shared/modules/payments/common/CardNumberInput/CardNumberInput.props";
import { useTranslation } from "@shared/utils/internationalization";

// eslint-disable-next-line import/no-cycle
import AuthorizeElement from "../AuthorizeElement/AuthorizeElement.web";

export type AuthorizeCardNumberElementProps = {
  /**
   * Fires when a value is well-formed and potentially complete.
   * It is not an indicator of whether a customer is done with their input --
   * it only indicates that the Element contains a potentially complete, well-formed value.
   */
  onValidField?: () => void;
  /**
   * Fires when a value is deemed invalid
   */
  onInvalidField?: () => void;
};

/**
 * A containing element for a an AuthorizeElement component configured as a cardNumber element.
 */
function AuthorizeCardNumberElement({
  onValidField,
  onInvalidField,
}: AuthorizeCardNumberElementProps) {
  const { t } = useTranslation("checkout");

  const [brand, setBrand] = useState<CardBrandLogoProps["brand"] | "unknown">(
    "unknown"
  );
  const [error, setError] = useState("");

  const handleChange = useCallback(
    (evt: CardNumberChangeEvent) => {
      setBrand(evt.brand?.brand || "unknown");

      if (evt.error) {
        setError(evt.error.message);
      } else if (!evt.error) {
        setError("");
      }

      if (evt.complete) {
        onValidField?.();
      } else {
        onInvalidField?.();
      }
    },
    [onValidField, onInvalidField]
  );

  return (
    <Box>
      <Box>
        <Text fontWeight={700} mb="2xs" fontSize="0.875rem">
          {t("Card number")}
        </Text>
      </Box>
      <Box
        border="1px solid"
        borderColor={!error ? "border" : "red.600"}
        borderRadius="sm"
        padding="0px 12px"
        bg="bg"
        position="relative"
      >
        <Box width="calc(100% - 24px)" paddingRight="sm" lineHeight="1.2em">
          <AuthorizeElement element="cardNumber" onChange={handleChange} />
        </Box>
        <Box
          overflow="hidden"
          position="absolute"
          top={0}
          right={0}
          width="element.4xl"
          height="100%"
          display="flex"
          alignItems="center"
          paddingLeft="sm"
          paddingRight="sm"
        >
          {brand && brand !== "unknown" ? (
            <Box
              animation="slideLeft 0.5s forwards"
              data-testid="authorize-element-cardNumber-brand-logo-container"
            >
              <CardBrandLogo brand={brand} />
            </Box>
          ) : null}
        </Box>
      </Box>
      <Box>
        <Text
          color="red.600"
          noOfLines={2}
          data-testid="authorize-element-cardNumber-error"
        >
          {error}
        </Text>
      </Box>
    </Box>
  );
}

export default AuthorizeCardNumberElement;
